// Website UI building blocks (black & gold). Styles live in site.css.
import React from 'react';
import { Link } from 'react-router-dom';

const cx = (...parts) => parts.filter(Boolean).join(' ');

export function Container({ as: Tag = 'div', className, children, ...rest }) {
  return <Tag className={cx('lx-container', className)} {...rest}>{children}</Tag>;
}

export function Eyebrow({ children, className }) {
  return <span className={cx('lx-eyebrow', className)}>{children}</span>;
}

// Eyebrow + serif title + optional lead paragraph.
export function SectionHeading({ eyebrow, title, lead, center, level = 2, className }) {
  const Title = `h${level}`;
  return (
    <div className={cx('lx-section-heading', center && 'lx-section-heading--center', className)}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <Title className={level === 1 ? 'lx-h1' : 'lx-h2'}>{title}</Title>
      {lead && <p className="lx-lead">{lead}</p>}
    </div>
  );
}

// Gold capsule (primary), gold outline (secondary) or text (ghost).
// Renders a router Link with `to`, an <a> with `href`, otherwise a <button>.
export function Button({
  variant = 'primary',
  size,
  block,
  icon: Icon,
  iconAfter: IconAfter,
  loading,
  to,
  href,
  className,
  children,
  type = 'button',
  disabled,
  ...rest
}) {
  const classes = cx(
    'lx-btn',
    `lx-btn--${variant}`,
    size && `lx-btn--${size}`,
    block && 'lx-btn--block',
    className
  );
  const content = (
    <>
      {loading ? <span className="lx-spinner" aria-hidden /> : Icon && <Icon aria-hidden />}
      <span>{children}</span>
      {IconAfter && !loading && <IconAfter aria-hidden />}
    </>
  );
  if (to) return <Link to={to} className={classes} {...rest}>{content}</Link>;
  if (href) return <a href={href} className={classes} {...rest}>{content}</a>;
  return (
    <button type={type} className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {content}
    </button>
  );
}

export function RoundButton({ label, icon: Icon, className, ...rest }) {
  return (
    <button type="button" className={cx('lx-round-btn', className)} aria-label={label} title={label} {...rest}>
      <Icon aria-hidden />
    </button>
  );
}

// Underline field with an uppercase label (iOS ModernTextField).
export function Field({ label, hint, error, htmlFor, className, children }) {
  return (
    <div className={cx('lx-field', error && 'lx-field--error', className)}>
      {label && <label className="lx-field-label" htmlFor={htmlFor}>{label}</label>}
      {children}
      {error ? <span className="lx-field-error" role="alert">{error}</span> : hint && <span className="lx-field-hint">{hint}</span>}
    </div>
  );
}

export const Input = React.forwardRef(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cx('lx-input', className)} {...props} />;
});

export const Textarea = React.forwardRef(function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} className={cx('lx-input', className)} {...props} />;
});

export const Select = React.forwardRef(function Select({ className, children, ...props }, ref) {
  return <select ref={ref} className={cx('lx-input', className)} {...props}>{children}</select>;
});

export function Card({ as: Tag = 'div', interactive, className, children, ...rest }) {
  return <Tag className={cx('lx-card', interactive && 'lx-card--interactive', className)} {...rest}>{children}</Tag>;
}

// Booking + ride status, mirroring iOS RideStatusPill and the live ride states.
const RIDE_STATUS = {
  assigned: ['Chauffeur assigned', 'gold'],
  en_route: ['On the way', 'live'],
  arrived: ['Arrived', 'live'],
  on_board: ['On board', 'live'],
};

export function bookingStatus(booking = {}) {
  if (booking.status === 'completed' || booking.ride_status === 'completed') return ['Completed', 'neutral'];
  if (booking.status === 'cancelled') {
    return booking.payment_status === 'pending' ? ['Awaiting payment', 'danger'] : ['Cancelled', 'danger'];
  }
  if (RIDE_STATUS[booking.ride_status]) return RIDE_STATUS[booking.ride_status];
  return ['Confirmed', 'gold'];
}

export function StatusPill({ label, tone = 'gold', booking }) {
  const [text, color] = booking ? bookingStatus(booking) : [label, tone];
  return <span className={`lx-pill lx-pill--${color}`}>{text}</span>;
}
